import React, {useEffect, useState} from "react"
import ProfileHeader from "../Headers/ProfileHeader"
import {Dropdown, DropdownButton, FormLabel} from "react-bootstrap"
import {
  deleteAssistant, deleteFileFromAssistant,
  getAssistant,
  getAssistantFiles,
  getAssistants,
  newAssistant,
  putAssistant,
} from "../../lib/api"
import {EditableText, EditableTextArea} from "../Shared/EditableWidgets"
import FileUploader from "./FileUploader";
import {TrashButton, XCircleButton} from "../Shared/Buttons";

function DropDownAssistants(props: {
  setAssistant: any,
  assistants: Assistant[],
  assistant: NullableAssistant,
  setAssistants: any,
  add: any,
  toggleInstructions: any,
  isNetwork: boolean
}) {
  const {assistants, setAssistant, assistant, setAssistants, add, toggleInstructions, isNetwork} = props
  const items = assistants.map((assistant: Assistant) =>
    <Dropdown.Item key={`assistant-${assistant.id}`}
                   onClick={() => setAssistant(assistant)}>{assistant.name}</Dropdown.Item>
  )
  const title = assistant ? assistant.name : "Choose assistant to edit"

  function delAssistant() {
    const answer = window.confirm("Are you sure you want to delete this assistant?")
    if (!answer) {
      return
    }
    deleteAssistant(assistant!.id).then(response => {
      if (response.ok) {
        response.json().then(json => {
          setAssistants(json.assistants)
          setAssistant(null)
        })
      }
    })
  }

  return (
    <div className="row flex-nowrap align-items-end align-items-baseline">
      <div className="col-1">
        <div className="col text-center pl-0">
          <a href="/instructions" className="underline" onClick={toggleInstructions}>Instructions</a>
        </div>
      </div>
      <div className="col-10">
        <Dropdown onClick={(e: any) => e.stopPropagation()} className="pl-4">
          <DropdownButton menuAlign="left" title={title}>
            {items}
            <Dropdown.Divider/>
            <Dropdown.Item key="create_assistant" onClick={add}>
              New Assistant…
            </Dropdown.Item>
          </DropdownButton>
        </Dropdown>
      </div>
      {assistant ? (
        <div className="col-1 text-right">
          <TrashButton onClick={delAssistant} visible/><br/>
          <div className={`${isNetwork ? 'd-block' : 'd-none'}`}>
            Saving...
          </div>
        </div>
      ) : null}
    </div>
  )
}

export interface Assistant {
  id: string
  assistantId: string
  name: string
  instructions: string
  model: string
  fileIds: number[]
  metadata: any
  tools: any
  userId: string
  createdAt: string
  updatedAt: string
}

export type NullableAssistant = Assistant | null

function EditAssistant(props: { assistant: Assistant, setAssistant: any, isNetwork: boolean, setIsNetwork: any }) {
  const {assistant, setAssistant, isNetwork, setIsNetwork} = props
  const [name, setName] = useState(assistant.name)
  const [instructions, setInstructions] = useState(assistant.instructions)
  const [files, setFiles] = useState([])
  const [error, setError] = useState<string | null>(null)
  const [enableRetrieval, setEnableRetrieval] = useState(false)
  const [enableCodeInterpreter, setEnableCodeInterpreter] = useState(false)

  useEffect(() => {
    getAssistant(assistant.id).then(response => {
      if (response.ok) {
        response.json().then(json => {
          setAssistant(json.assistant)
          setName(json.assistant.name)
          setInstructions(json.assistant.instructions)
          setEnableCodeInterpreter(json.assistant.tools.some((feature: any) => feature.type === "code_interpreter"))
          setEnableRetrieval(json.assistant.tools.some((feature: any) => feature.type === "retrieval"))
        })
      }
    })

    refreshAssistantFiles()
  }, [assistant.id])

  function refreshAssistantFiles() {
    getAssistantFiles(assistant.id).then(response => {
      console.log('files', response)
      if (response.ok) {
        response.json().then(json => {
          setFiles(json.files)
        })
      }
    })
  }

  function delFile(fileId: string) {
    const answer = window.confirm("Are you sure you want to remove this file from this assistant?")
    if (!answer) {
      return
    }
    deleteFileFromAssistant(fileId, assistant.id).then(response => {
      if (response.ok) {
        response.json().then(json => {
          setFiles(json.files)
        })
      }
    })
  }


  function FileList(props: { files: any }) {
    const {files} = props
    const list = files.map((file: any) => {
      return (<li key={`file-${file.id}`}><XCircleButton onClick={() => delFile(file.id)}
                                                         visible/> {file.filename}, {file.status}</li>)
    })

    return (<ul>{list}</ul>)
  }


  function toggleTool(feature: string) {
    const backupRetrieval = enableRetrieval
    const backupCodeInterpreter = enableCodeInterpreter
    const backupAssistant = {...assistant}

    const newEnableRetrieval = feature === "retrieval" ? !enableRetrieval : enableRetrieval
    const newEnableCodeInterpreter = feature === "code_interpreter" ? !enableCodeInterpreter : enableCodeInterpreter

    // Update state to reflect new values
    setEnableRetrieval(newEnableRetrieval)
    setEnableCodeInterpreter(newEnableCodeInterpreter)

    // Construct tools array based on new state values
    const tools = []
    if (newEnableCodeInterpreter) {
      tools.push({"type": "code_interpreter"});
    }
    if (newEnableRetrieval) {
      tools.push({"type": "retrieval"});
    }

    // Set assistant tools in state and attempt to update assistant data
    const updatedAssistant = {...assistant, tools: [...tools]};
    setAssistant(updatedAssistant);

    // Log for debugging
    console.log(tools);
    console.log(updatedAssistant);

    // Now use updatedAssistant for the PUT request
    setIsNetwork(true)
    putAssistant(updatedAssistant).then(response => {
      setIsNetwork(false)
      if (!response.ok) {
        // Revert state if update fails
        setEnableRetrieval(backupRetrieval);
        setEnableCodeInterpreter(backupCodeInterpreter);
        setAssistant(backupAssistant);
      }
    })
  }

  function fileUploaded(response: Response) {
    // TODO:
    if (response.ok) {
      refreshAssistantFiles()
    } else {
      response.json().then(json => {
        setError(json.error || 'An unknown error occurred')
      })
    }
  }

  function useDebounce(value: any, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const debouncedName = useDebounce(name, 1000);
  const debouncedInstructions = useDebounce(instructions, 1000);

  useEffect(() => {
    function setNameAndPut(name: string) {
      // TODO: setAssistants([...assistants
      setIsNetwork(true)
      setAssistant({...assistant, ...{name: name}})
      putAssistant({...assistant, ...{name: name}}).then(response => {
          setIsNetwork(false)
          if (!response.ok) {
            // TODO
          }
        }
      )
    }

    if (debouncedName) setNameAndPut(debouncedName)
  }, [debouncedName]); // Effect depends on debouncedName

  useEffect(() => {
    function setInstructionsAndPut(instructions: string) {
      setAssistant({...assistant, ...{instructions: instructions}})
      setIsNetwork(true)
      putAssistant({...assistant, ...{instructions: instructions}}).then(response => {
          setIsNetwork(false)
          if (!response.ok) {
            // TODO
          }
        }
      )
    }

    if (debouncedInstructions) setInstructionsAndPut(debouncedInstructions)
  }, [debouncedInstructions]); // Effect depends on debouncedInstructions

  return (
    <>
      <div className="row pt-4">
        <div className="col pb-3">
          <FormLabel className="text-left">Name</FormLabel>
          <EditableText value={name}
                        ariaLabel="Name"
                        isEditable={true}
                        isEditing={true}
                        maxLength={512}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                        placeholder="Name of this Assistant"
                        setIsEditing={() => {
                        }}/>
        </div>
      </div>
      <div className="row">
        <div className="col pb-3">
          <FormLabel className="text-left">Instructions</FormLabel>
          <EditableTextArea value={instructions || ''}
                            ariaLabel="Instructions"
                            isEditable={true}
                            isEditing={true}
                            maxLength={32768}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setInstructions(event.target.value)}
                            placeholder="The system instructions that the assistant uses"
                            setIsEditing={() => {
                            }} rows={5}/>
        </div>
      </div>
      <div className="row">
        <div className="col pb-3">
          Code Interpreter <input type="checkbox" checked={enableCodeInterpreter}
                                  onChange={(event) => toggleTool("code_interpreter")}/>
          <span className="slider round"></span>
        </div>
      </div>
      <div className="row">
        <div className="col pb-3">
          Retrieval <input type="checkbox" checked={enableRetrieval} onChange={(event) => toggleTool("retrieval")}/>
          <span className="slider round"></span>
        </div>
      </div>
      <div className="row">
        <div className="col pb-3">
          <FormLabel className="text-left">Files</FormLabel><FileUploader assistant={assistant}
                                                                          fileUploaded={fileUploaded}
                                                                          setError={setError}
                                                                          setIsNetwork={setIsNetwork}/>
          {error ? <div className="text-danger">{error}</div> : null}
          <FileList files={files}/>
        </div>
      </div>
    </>
  )
}


function Assistants(props: {
  cognitoUser: any
}) {
  const {cognitoUser} = props
  const [assistants, setAssistants] = useState([])
  const [assistant, setAssistant] = useState<NullableAssistant>(null)
  const [showInstructions, setShowInstructions] = useState(false)
  const [isNetwork, setIsNetwork] = useState(false)

  useEffect(() => {
    getAssistants().then(response => {
      {
        if (response.ok) {
          response.json().then(json => {
            console.log(json)
            setAssistants(json.assistants)
          })
        }
      }
    })
  }, [])

  function toggleInstructions(event: any) {
    setShowInstructions(!showInstructions)
    event.preventDefault()
  }

  function select(assistant: Assistant) {
    setAssistant(assistant)
  }

  function add() {
    newAssistant().then(response => {
      if (response.ok) {
        response.json().then(json => {
          setAssistants(json.assistants)
          setAssistant(json.assistant)
        });
      }
    });
  }

  return (
    <>
      <ProfileHeader cognitoUser={cognitoUser} title="Assistants" linkToHome="/home"/>
      <section className="container main assistants p-4 rounded">
        <div className="row">
          <div className="col text-center">
            <DropDownAssistants assistants={assistants}
                                assistant={assistant}
                                setAssistant={setAssistant}
                                setAssistants={setAssistants}
                                add={add}
                                toggleInstructions={toggleInstructions}
                                isNetwork={isNetwork}
            />
            {assistant ? <EditAssistant assistant={assistant}
                                        setAssistant={setAssistant}
                                        isNetwork={isNetwork}
                                        setIsNetwork={setIsNetwork}
            /> : null}
          </div>
        </div>
        {/*<AssistantInstructions show={showInstructions}*/}
        {/*                           onHide={() => setShowInstructions(false)}/>*/}
      </section>
    </>
  )
}

export default Assistants
